import { Link } from "react-router-dom";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Save, Loader2 } from "lucide-react";
import { useEditPage } from "@/hooks/useEditPage";
import ErrorBanner from "@/components/common/ErrorBanner";
import SaveSuccessIndicator from "@/components/common/SaveSuccessIndicator";
import ValidationErrorBanner from "@/components/common/ValidationErrorBanner";
import { premiumUpgradeCreateSchema } from "@ckgo2/shared/schemas";
import type { PremiumUpgrade } from "@ckgo2/shared/types";
import MediaPicker from "@/components/common/MediaPicker";

interface FormData {
  title: string;
  description: string;
  imageId: string | null;
  badge: string;
  sortOrder: number;
  isActive: boolean;
}

export default function PremiumUpgradeEditPage() {
  const { t } = useTranslation();
  const { isNew, isLoading, form, saveMutation, onSubmit, itemData } = useEditPage<
    FormData,
    PremiumUpgrade
  >({
    resourceKey: "premiumUpgrade",
    listKey: "premiumUpgrades",
    apiPath: "/api/admin/premium-upgrades",
    schema: premiumUpgradeCreateSchema,
    defaultValues: {
      title: "",
      description: "",
      imageId: null,
      badge: "",
      sortOrder: 0,
      isActive: true,
    },
    navigateTo: "/premium-upgrades",
    transformOnLoad: (u) => ({
      title: u.title || "",
      description: u.description || "",
      imageId: u.imageId ?? null,
      badge: u.badge || "",
      sortOrder: u.sortOrder || 0,
      isActive: u.isActive ?? true,
    }),
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { isSubmitting, errors },
  } = form;

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    );
  }

  if (!isNew && !isLoading && !itemData) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <p className="text-lg text-gray-600 dark:text-gray-400">
          {t("finance.premiumUpgrades.form.notFound")}
        </p>
        <Link to="/premium-upgrades" className="btn-primary">
          <ArrowLeft className="h-5 w-5" />
          {t("common.actions.back")}
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/premium-upgrades"
            className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {isNew
                ? t("finance.premiumUpgrades.form.newTitle")
                : t("finance.premiumUpgrades.form.editTitle")}
            </h1>
            <p className="mt-1 text-gray-600 dark:text-gray-400">
              {isNew
                ? t("finance.premiumUpgrades.form.newSubtitle")
                : t("finance.premiumUpgrades.form.editingSubtitle", {
                    title: itemData?.title ?? "",
                  })}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <SaveSuccessIndicator isSuccess={saveMutation.isSuccess} />
          <button
            type="button"
            onClick={handleSubmit(onSubmit)}
            disabled={isSubmitting}
            className="btn-primary"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                {t("common.actions.saving")}
              </>
            ) : (
              <>
                <Save className="h-5 w-5" />
                {t("common.actions.save")}
              </>
            )}
          </button>
        </div>
      </div>

      <ErrorBanner error={saveMutation.error} />
      <ValidationErrorBanner errors={errors} />

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <div className="card space-y-4">
            <div>
              <label htmlFor="title" className="label">
                {t("common.fields.title")} *
              </label>
              <input
                {...register("title")}
                type="text"
                id="title"
                className="input"
                placeholder={t("finance.premiumUpgrades.form.titlePlaceholder")}
              />
            </div>

            <div>
              <label htmlFor="description" className="label">
                {t("common.fields.description")}
              </label>
              <textarea
                {...register("description")}
                id="description"
                rows={4}
                className="input"
                placeholder={t("finance.premiumUpgrades.form.descriptionPlaceholder")}
              />
            </div>

            <div>
              <label className="label">{t("common.fields.image")}</label>
              <Controller
                name="imageId"
                control={control}
                render={({ field }) => (
                  <MediaPicker
                    mode="single"
                    value={field.value ?? null}
                    onChange={field.onChange}
                    folder="general"
                  />
                )}
              />
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="card space-y-4">
            <div>
              <label htmlFor="badge" className="label">
                {t("finance.premiumUpgrades.badge")}
              </label>
              <select {...register("badge")} id="badge" className="input">
                <option value="">{t("finance.premiumUpgrades.form.noBadge")}</option>
                <option value="POPULAR">{t("finance.premiumUpgrades.badges.popular")}</option>
                <option value="OUR TIP">{t("finance.premiumUpgrades.badges.ourTip")}</option>
                <option value="DISCOUNT">{t("finance.premiumUpgrades.badges.discount")}</option>
                <option value="NEW">{t("finance.premiumUpgrades.badges.new")}</option>
              </select>
            </div>

            <div>
              <label htmlFor="sortOrder" className="label">
                {t("common.fields.sortOrder")}
              </label>
              <input
                {...register("sortOrder", { valueAsNumber: true })}
                type="number"
                id="sortOrder"
                className="input"
              />
            </div>

            <div className="flex items-center gap-3">
              <input
                {...register("isActive")}
                type="checkbox"
                id="isActive"
                className="text-primary-600 focus:ring-primary-500 h-4 w-4 rounded border-gray-300"
              />
              <label
                htmlFor="isActive"
                className="text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                {t("common.status.active")}
              </label>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
